import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import { CopilotPanelEntityRef } from 'models/copilot/copilotPanelContextDefinition';
import { CopilotProjectsContent } from 'models/copilot/copilotProjects';
import { EntityTypes } from 'models/entity';
import { Iso27001Rooms } from 'models/projects/iso27001Rooms';
import { CopilotGenericPanelContextDefinition } from '../copilotGenericPanelContextDefinition';
import {
  isProjectListRoom,
  projectDocumentsSummaryTemplate,
  projectObjectivesKeepOrArchiveTemplate,
  projectOverviewAuditorTemplate,
  projectProfileAuditorTemplate,
  projectRisksWhyHereTemplate,
  projectRolesMandatoryTemplate,
  projectScopeCheckTemplate,
  projectSummarizeRoomTemplate,
  projectWhatNowTemplate,
} from './projectsAIActionTemplates';

// The project scene usage: plain chat with the project (or the room inside it) as its subject, plus a
// set of ready-made questions per surface. There is no process and nothing is written: the answer path
// is the generic one, and the backend routes this usage's context key to the project agent, which
// briefs the model from the project's own records and streams its answer to the panel.
export class CopilotProjectsPanelContextDefinition extends CopilotGenericPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.Projects;
    this.name = 'Projects';
    this.supportsChat = true;
  }

  /**
   * The grounding every question ends with: the project (and the room, when one is open) by name, so
   * the agent starts on the right subject. Without a framework in the cache there is nothing to put
   * between the brackets, so the grounding names the project (and the room) alone rather than
   * trailing an empty pair. Built here because the sentence is the usage's wording, not a template's.
   */
  private static getGrounding(t: TFunction<string[]>, content: CopilotProjectsContent): string {
    const project: string = content.projectName;
    const room: string = content.roomName ?? '';
    const standards: string = content.standardNames.join(', ');
    const hasStandards: boolean = content.standardNames.length > 0;

    if (content.roomKey === undefined) {
      return hasStandards
        ? t('copilot:Actions.Projects.GroundingProject', { project: project, standards: standards })
        : t('copilot:Actions.Projects.GroundingProjectNoStandard', { project: project });
    }

    return hasStandards
      ? t('copilot:Actions.Projects.GroundingRoom', { room: room, project: project, standards: standards })
      : t('copilot:Actions.Projects.GroundingRoomNoStandard', { room: room, project: project });
  }

  /**
   * The project scene's ready-made questions: one general one that fits every surface, plus at most
   * one that belongs to the surface the user is on, plus the room summary on the four list rooms.
   * Every binding routes to the page its content came from, so a scene menu lists it there only.
   */
  getBindings(content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    if (!(content instanceof CopilotProjectsContent)) return [];

    // The grounding is wording and needs `t`; a binding's label and prompt are resolved with `t` when
    // the menu or the panel reads them, so the params carry a resolver instead of the sentence.
    const options = { route: content.route };
    const grounding = (t: TFunction<string[]>) => CopilotProjectsPanelContextDefinition.getGrounding(t, content);
    const bindings: AIActionBinding[] = [
      createAIActionBinding(
        projectWhatNowTemplate,
        {
          getGrounding: grounding,
          roomName: content.roomKey === undefined ? undefined : content.roomName ?? '',
          projectName: content.projectName,
        },
        options,
      ),
    ];

    if (content.roomKey === undefined) {
      bindings.push(
        createAIActionBinding(
          projectOverviewAuditorTemplate,
          { getGrounding: grounding, milestoneName: content.milestoneName ?? '' },
          options,
        ),
      );

      return bindings;
    }

    // One room, one question: the rooms this standard owns each have their own, and a room without
    // one keeps the general question rather than borrowing another room's.
    switch (content.roomKey) {
      case Iso27001Rooms.Profile:
        bindings.push(createAIActionBinding(projectProfileAuditorTemplate, { getGrounding: grounding }, options));
        break;
      case Iso27001Rooms.Scope:
        bindings.push(createAIActionBinding(projectScopeCheckTemplate, { getGrounding: grounding }, options));
        break;
      case Iso27001Rooms.Objectives:
        bindings.push(
          createAIActionBinding(projectObjectivesKeepOrArchiveTemplate, { getGrounding: grounding }, options),
        );
        break;
      case Iso27001Rooms.Risks:
        bindings.push(createAIActionBinding(projectRisksWhyHereTemplate, { getGrounding: grounding }, options));
        break;
      case Iso27001Rooms.Roles:
        bindings.push(createAIActionBinding(projectRolesMandatoryTemplate, { getGrounding: grounding }, options));
        break;
      case Iso27001Rooms.Documents:
        bindings.push(createAIActionBinding(projectDocumentsSummaryTemplate, { getGrounding: grounding }, options));
        break;
    }

    // The four list rooms can be summarized in the panel, whole or by the rows the reader selected.
    if (isProjectListRoom(content.roomKey)) {
      bindings.push(
        createAIActionBinding(
          projectSummarizeRoomTemplate,
          { getGrounding: grounding, roomKey: content.roomKey },
          options,
        ),
      );
    }

    return bindings;
  }

  // The room's anchor task is the more precise subject: through it the agent reaches the room's own
  // rows, its linked documents and its follow-ups. Without a room the project itself is the subject.
  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!(content instanceof CopilotProjectsContent)) return undefined;

    return content.deliverableTaskId
      ? { entityType: EntityTypes.Task, entityId: content.deliverableTaskId }
      : { entityType: EntityTypes.Project, entityId: content.projectId };
  }

  // The menu label of the summarize action with rows selected: the rows the reader selected in the open room,
  // counted. An empty selection is the whole room and is worded by the action itself, so it has no wording here.
  getEntitySelectionQuestion(selectedEntityIds: number[], t: TFunction<string[]>): string | undefined {
    if (selectedEntityIds.length === 0) return undefined;

    return t('copilot:Actions.Projects.SummarizeSelected', { count: selectedEntityIds.length });
  }
}
